// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const authRequestIdRef = useRef(0);

  const clearAuthData = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('username');
    setCurrentUser(null);
  }, []);

  const refreshUserData = useCallback((token) => {
    if (!token) {
      clearAuthData();
      setAuthLoading(false);
      return;
    }

    const requestId = ++authRequestIdRef.current;

    api
      .get('/api/users/profile', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (requestId !== authRequestIdRef.current) return;

        const userData = response.data;

        if (userData && !userData.message) {
          const finalize = (ud) => {
            if (requestId !== authRequestIdRef.current) return;

            const userWithToken = { ...ud, token };
            setCurrentUser(userWithToken);
            localStorage.setItem('userData', JSON.stringify(userWithToken));

            if (ud.username) {
              localStorage.setItem('username', ud.username);
            }
          };

          if (userData.stripeAccountId) {
            api
              .get(`/api/payments/account-status?userId=${userData.id}`)
              .then((statusRes) => {
                if (requestId !== authRequestIdRef.current) return;
                userData.stripeAccountStatus = statusRes.data.stripeAccountStatus;
                finalize(userData);
              })
              .catch(() => {
                finalize(userData);
              })
              .finally(() => {
                if (requestId === authRequestIdRef.current) {
                  setAuthLoading(false);
                }
              });
          } else {
            finalize(userData);
            setAuthLoading(false);
          }
        } else {
          console.error('Error fetching user data:', userData?.message);
          clearAuthData();
          setAuthLoading(false);
        }
      })
      .catch((err) => {
        if (requestId !== authRequestIdRef.current) return;
        console.error('Error fetching user data:', err);
        clearAuthData();
        setAuthLoading(false);
      });
  }, [clearAuthData]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (token) {
      refreshUserData(token);
    } else if (refreshToken) {
      api
        .post('/api/users/refresh-token', { refreshToken })
        .then((response) => {
          const newAccessToken = response.data?.accessToken;
          const newRefreshToken = response.data?.refreshToken;

          if (!newAccessToken) {
            throw new Error('No access token returned while restoring session');
          }

          localStorage.setItem('accessToken', newAccessToken);

          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          refreshUserData(newAccessToken);
        })
        .catch((err) => {
          console.error('Error restoring session:', err);
          clearAuthData();
          setAuthLoading(false);
        });
    } else {
      setAuthLoading(false);
    }
  }, [clearAuthData, refreshUserData]);

  useEffect(() => {
    const handleTokenRefresh = (e) => {
      const newAccessToken = e.detail?.accessToken;
      const newRefreshToken = e.detail?.refreshToken;

      if (!newAccessToken) return;

      localStorage.setItem('accessToken', newAccessToken);

      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      refreshUserData(newAccessToken);
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
    };
  }, [refreshUserData]);

  const loginUser = (userData, accessToken, refreshToken) => {
    authRequestIdRef.current += 1;

    if (!accessToken) {
      console.error('loginUser called without an access token');
      clearAuthData();
      return;
    }

    localStorage.setItem('accessToken', accessToken);

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }

    const userWithToken = { ...userData, token: accessToken };
    localStorage.setItem('userData', JSON.stringify(userWithToken));

    if (userData?.username) {
      localStorage.setItem('username', userData.username);
    } else {
      localStorage.removeItem('username');
    }

    setCurrentUser(userWithToken);
    setAuthLoading(false);
  };

  const updateCurrentUser = (userData) => {
    const token = localStorage.getItem('accessToken') || currentUser?.token;

    if (!token) {
      console.error('updateCurrentUser called without access token');
      return;
    }

    const userWithToken = {
      ...userData,
      token,
    };

    setCurrentUser(userWithToken);
    localStorage.setItem('userData', JSON.stringify(userWithToken));

    if (userData?.username) {
      localStorage.setItem('username', userData.username);
    }
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    authRequestIdRef.current += 1;
    clearAuthData();
    setAuthLoading(false);

    if (!refreshToken) {
      return;
    }

    try {
      await api.post('/api/users/logout', { refreshToken });
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        authLoading,
        loginUser,
        logout,
        refreshUserData,
        updateCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
